/*
 * Gratia motion: line illustrations that draw themselves, layered depth that
 * drifts with scroll, and a gentle pointer-follow on the hero. Everything is
 * skipped under prefers-reduced-motion and in the theme editor.
 *
 * Hooks: [data-draw] (container whose .gr-illo drawings draw in when revealed),
 *        [data-depth="0.3"] (layer offset factor), [data-gr-hero] (pointer area),
 *        [data-scrub] (verse text whose .gr-word spans light up with scroll).
 */
(() => {
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  if (reduceMotion.matches || window.Shopify?.designMode) return;
  const root = document.documentElement;
  root.classList.add('gr-motion');

  /* ---------- Draw-on ---------- */

  const prepareDrawing = (svg) => {
    svg.querySelectorAll('path, circle, ellipse, line, polyline').forEach((shape) => {
      if (typeof shape.getTotalLength !== 'function') return;
      const length = Math.ceil(shape.getTotalLength());
      shape.style.strokeDasharray = `${length} ${length}`;
      shape.style.strokeDashoffset = String(length);
    });
    svg.dataset.drawReady = '';
  };

  const drawIn = (container) => {
    container.querySelectorAll('svg.gr-illo').forEach((svg, i) => {
      svg.querySelectorAll('path, circle, ellipse, line, polyline').forEach((shape, j) => {
        shape.style.transition = `stroke-dashoffset 1600ms cubic-bezier(0.65, 0, 0.35, 1) ${i * 120 + j * 90}ms`;
        shape.style.strokeDashoffset = '0';
      });
    });
  };

  const drawObserver = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        drawIn(entry.target);
        drawObserver.unobserve(entry.target);
      }
    },
    { threshold: 0.35 }
  );

  document.querySelectorAll('[data-draw]').forEach((container) => {
    container.querySelectorAll('svg.gr-illo').forEach(prepareDrawing);
    drawObserver.observe(container);
  });

  /* ---------- Depth: scroll drift + pointer follow ---------- */

  const layers = [...document.querySelectorAll('[data-depth]')];
  const hero = document.querySelector('[data-gr-hero]');
  const pointer = { x: 0, y: 0, tx: 0, ty: 0 };
  const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  let frame = 0;

  const render = () => {
    frame = 0;
    // Ease the pointer toward its target so movement feels soft, not glued to the mouse.
    pointer.x += (pointer.tx - pointer.x) * 0.08;
    pointer.y += (pointer.ty - pointer.y) * 0.08;
    const heroTop = hero ? hero.getBoundingClientRect().top : 0;
    const scrolled = Math.max(0, -heroTop);
    for (const layer of layers) {
      const depth = Number(layer.dataset.depth) || 0;
      const x = pointer.x * depth * 26;
      const y = scrolled * depth * 0.35 + pointer.y * depth * 16;
      layer.style.transform = `translate3d(${x.toFixed(2)}px, ${y.toFixed(2)}px, 0)`;
    }
    if (Math.abs(pointer.tx - pointer.x) > 0.001 || Math.abs(pointer.ty - pointer.y) > 0.001) schedule();
  };

  const schedule = () => {
    if (!frame) frame = requestAnimationFrame(render);
  };

  // Horizon scrolls .page-wrapper on desktop; capture-phase listening hears either scroller.
  document.addEventListener('scroll', schedule, { passive: true, capture: true });
  if (hero && finePointer) {
    hero.addEventListener('pointermove', (event) => {
      const rect = hero.getBoundingClientRect();
      pointer.tx = (event.clientX - rect.left) / rect.width - 0.5;
      pointer.ty = (event.clientY - rect.top) / rect.height - 0.5;
      schedule();
    });
    hero.addEventListener('pointerleave', () => {
      pointer.tx = 0;
      pointer.ty = 0;
      schedule();
    });
  }
  schedule();

  /* ---------- Verse scrub ---------- */

  document.querySelectorAll('[data-scrub]').forEach((text) => {
    const words = [...text.querySelectorAll('.gr-word')];
    text.classList.add('is-scrubbing');
    let pending = 0;
    const update = () => {
      pending = 0;
      const rect = text.getBoundingClientRect();
      const vh = window.innerHeight;
      const progress = Math.min(1, Math.max(0, (vh * 0.85 - rect.top) / (vh * 0.5)));
      const lit = Math.round(progress * words.length);
      words.forEach((word, i) => word.classList.toggle('is-lit', i < lit));
    };
    document.addEventListener('scroll', () => { if (!pending) pending = requestAnimationFrame(update); }, { passive: true, capture: true });
    update();
  });

  reduceMotion.addEventListener?.('change', (event) => {
    if (!event.matches) return;
    root.classList.remove('gr-motion');
    layers.forEach((layer) => (layer.style.transform = ''));
    document.querySelectorAll('svg.gr-illo [style]').forEach((shape) => shape.removeAttribute('style'));
  });
})();
